import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Stack } from "@mantine/core";
import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";
import Textarea from "@/components/atoms/Textarea";
import CTA from "@/components/atoms/CTA";

interface RejectRegistrationModalProps {
  opened: boolean;
  loading: boolean;
  onClose: () => void;
  onReject: (comment: string) => void;
}

/** The backend requires a reason and emails it to the registrant, so send stays disabled until there is one. */
export default function RejectRegistrationModal({ opened, loading, onClose, onReject }: RejectRegistrationModalProps) {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (opened) setComment("");
  }, [opened]);

  const reason = comment.trim();

  return createPortal(
    <Modal opened={opened} onClose={onClose} title={t("commonAdmin:rejectRegistration.title")}>
      <ModalContent>
        <Stack gap="md" p="20px 23px">
          <Textarea
            data-testid="reject-reason-input"
            label={t("commonAdmin:rejectRegistration.reasonLabel")}
            description={t("commonAdmin:rejectRegistration.reasonDescription")}
            required
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
          />
        </Stack>
      </ModalContent>
      <ModalCTAs>
        <CTA intent="secondary" onClick={onClose} disabled={loading}>
          {t("actions:cancel")}
        </CTA>
        <CTA onClick={() => onReject(reason)} loading={loading} disabled={!reason} data-testid="reject-submit-btn">
          {t("actions:reject")}
        </CTA>
      </ModalCTAs>
    </Modal>,
    document.body
  );
}
